import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import type { AnalyticsKpiMetric, AnalyticsTrend } from "@/features/admin/adminAnalytics.types";

type KPICardProps = {
  metric: AnalyticsKpiMetric;
};

function formatValue(metric: AnalyticsKpiMetric) {
  if (metric.value == null) {
    return "Unavailable";
  }

  if (typeof metric.value === "string") {
    return metric.value;
  }

  if (metric.unit === "cents") {
    return new Intl.NumberFormat("en-US", {
      currency: "USD",
      style: "currency",
    }).format(metric.value / 100);
  }

  if (metric.unit === "usd") {
    return new Intl.NumberFormat("en-US", {
      currency: "USD",
      style: "currency",
    }).format(metric.value);
  }

  if (metric.unit === "percent") {
    return `${metric.value.toFixed(1)}%`;
  }

  return new Intl.NumberFormat("en-US").format(metric.value);
}

function trendClasses(quality: AnalyticsTrend["quality"]) {
  if (quality === "improving") {
    return "border-[#4c8f6a]/35 bg-[#1d4f39]/30 text-[#a7e4c2]";
  }
  if (quality === "deteriorating") {
    return "border-[#8f4f4c]/35 bg-[#5a2825]/30 text-[#f1b6ad]";
  }
  return "border-white/10 bg-white/5 text-[#d7cab9]/72";
}

export function KPICard({ metric }: KPICardProps) {
  const trend = metric.trend;
  const TrendIcon = trend?.direction === "up"
    ? ArrowUpRight
    : trend?.direction === "down"
      ? ArrowDownRight
      : ArrowRight;

  return (
    <article className="rounded-[1.35rem] border border-white/10 bg-black/20 px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#b69d7e]">
          {metric.label}
        </p>
        {metric.currentState ? (
          <span className="rounded-full border border-[#d2b793]/25 bg-[#d2b793]/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#e8c59b]">
            Live
          </span>
        ) : null}
      </div>
      <p className="mt-4 text-2xl font-semibold text-[#fff7ef]">{formatValue(metric)}</p>
      {trend ? (
        <div className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${trendClasses(trend.quality)}`}>
          <TrendIcon className="size-3.5" />
          {trend.changePercent == null ? "Comparison unavailable" : `${Math.abs(trend.changePercent).toFixed(1)}%`}
        </div>
      ) : null}
    </article>
  );
}
